import { Package, ExternalLink, Eye, ShoppingCart, Check, Trash2, Tag } from 'lucide-react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { Modal } from './Modal';
import type { ChecklistItem } from '../../types';
import { getIkeaImageUrl, getIkeaProductUrl } from '../../utils/helpers';

interface ItemDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: ChecklistItem | null;
  onToggleWatchlist: () => void;
  onStartPurchase: () => void;
  onToggleStatus: () => void;
  onDelete: () => void;
}

function StatusBadge({ status }: { status: string }) {
  const { t } = useLanguage();

  switch (status) {
    case 'installed':
      return (
        <span className="px-3 py-1 text-sm font-medium bg-green-100 text-green-700 rounded-full">
          {t('checklists.statusInstalled', 'Installed')}
        </span>
      );
    case 'purchased':
      return (
        <span className="px-3 py-1 text-sm font-medium bg-blue-100 text-blue-700 rounded-full">
          {t('checklists.statusPurchased', 'Purchased')}
        </span>
      );
    default:
      return (
        <span className="px-3 py-1 text-sm font-medium bg-gray-100 text-gray-600 rounded-full">
          {t('checklists.statusPending', 'Pending')}
        </span>
      );
  }
}

export function ItemDetailModal({
  isOpen,
  onClose,
  item,
  onToggleWatchlist,
  onStartPurchase,
  onToggleStatus,
  onDelete,
}: ItemDetailModalProps) {
  const { t } = useLanguage();

  if (!item) return null;

  const canPurchase = (item.quantityPurchased || 0) < (item.quantityNeeded || 1);
  const hasSavings = item.estimatedPrice && item.actualPrice && item.actualPrice < item.estimatedPrice;
  const savingsAmount = hasSavings
    ? (item.estimatedPrice! - item.actualPrice!) * (item.quantityNeeded || 1)
    : 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('checklists.itemDetails', 'Item Details')}
      icon={Package}
      iconColor="primary"
    >
      <div className="space-y-6">
        {/* Product image */}
        <div className="flex justify-center">
          {item.productCode ? (
            <div className="w-40 h-40 bg-gray-100 rounded-2xl flex items-center justify-center overflow-hidden">
              <img
                src={getIkeaImageUrl(item.productCode)}
                alt={item.name}
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="w-40 h-40 bg-gray-100 rounded-2xl flex items-center justify-center">
              <Package className="w-16 h-16 text-gray-400" />
            </div>
          )}
        </div>

        {/* Product info */}
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900">{item.name}</h3>
          {item.productCode && (
            <a
              href={getIkeaProductUrl(item.productCode)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 mt-1"
            >
              {item.productCode}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )}
        </div>

        {/* Details grid */}
        <div className="space-y-3 p-4 bg-gray-50 rounded-xl">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">{t('checklists.quantity', 'Quantity')}:</span>
            <span className="font-medium text-gray-900">
              {item.quantityPurchased || 0} / {item.quantityNeeded} {t('checklists.purchased', 'purchased')}
            </span>
          </div>

          {item.estimatedPrice != null && item.estimatedPrice > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('checklists.estimatedPrice', 'Estimated Price')}:</span>
              <span className="font-medium text-gray-900">${item.estimatedPrice.toFixed(2)}</span>
            </div>
          )}

          {item.actualPrice != null && item.actualPrice > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">{t('checklists.actualPrice', 'Actual Price')}:</span>
              <span className="font-medium text-gray-900">${item.actualPrice.toFixed(2)}</span>
            </div>
          )}

          <div className="flex justify-between text-sm items-center">
            <span className="text-gray-600">{t('checklists.status', 'Status')}:</span>
            <StatusBadge status={item.status} />
          </div>
        </div>

        {/* Savings badge */}
        {hasSavings && (
          <div className="flex items-center justify-center gap-2 p-3 bg-green-50 rounded-xl border border-green-100">
            <Tag className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-700">
              {t('checklists.saved', 'Saved')}: ${savingsAmount.toFixed(2)}
            </span>
          </div>
        )}

        {/* Action buttons */}
        <div className="space-y-2">
          {item.productCode && (
            <button
              onClick={onToggleWatchlist}
              className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium rounded-xl transition-colors ${
                item.onWatchlist
                  ? 'text-primary-600 bg-primary-50 hover:bg-primary-100'
                  : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
              }`}
            >
              <Eye className="w-4 h-4" />
              {item.onWatchlist ? t('checklists.watching', 'Watching') : t('checklists.addToWatchlist', 'Add to Watchlist')}
            </button>
          )}

          {canPurchase && (
            <button
              onClick={onStartPurchase}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-white bg-primary-600 rounded-xl hover:bg-primary-700 transition-colors shadow-sm"
            >
              <ShoppingCart className="w-4 h-4" />
              {t('checklists.recordPurchase', 'Record Purchase')}
            </button>
          )}

          <button
            onClick={onToggleStatus}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors"
          >
            <Check className="w-4 h-4" />
            {t('checklists.toggleStatus', 'Toggle Status')}
          </button>

          <button
            onClick={onDelete}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-red-600 bg-red-50 rounded-xl hover:bg-red-100 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
            {t('checklists.deleteItem', 'Delete Item')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
